import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ref, get } from "firebase/database";
import { db } from "../firebase";

// Cache for dashboard data
const dashboardCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export default function AreaDashboard() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [areaId, setAreaId] = useState(null);
  const [meta, setMeta] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const dataFetchedRef = useRef(false);

  // Fetch all data once with caching - Only fetch necessary data
  const fetchAllData = useCallback(async (forceRefresh = false) => {
    if (!slug) return;
    
    // Check cache first
    const cacheKey = `dashboard_${slug}`;
    const cachedData = dashboardCache.get(cacheKey);
    
    if (!forceRefresh && cachedData && (Date.now() - cachedData.timestamp) < CACHE_DURATION) {
      console.log("Using cached dashboard data");
      setAreaId(cachedData.areaId);
      setMeta(cachedData.meta);
      setCustomers(cachedData.customers);
      setLastUpdate(new Date(cachedData.timestamp));
      setLoading(false);
      setRefreshing(false);
      return;
    }
    
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const areasRef = ref(db, "areas");
      const snapshot = await get(areasRef);
      const val = snapshot.val() || {};
      
      const found = Object.entries(val).find(
        ([, v]) => v.meta && v.meta.slug === slug
      );
      
      if (found) {
        const areaIdValue = found[0];
        const metaValue = found[1].meta;
        const customersData = found[1].customers || {};
        
        // Only store minimal customer data for stats (not full details)
        const customersArray = Object.entries(customersData).map(([id, c]) => ({
          id,
          name: c.name,
          type: c.type,
          pendingAmount: Number(c.pendingAmount) || 0,
          advance: Number(c.advance) || 0,
          pendingJars: Number(c.pendingJars) || 0,
          securityMoney: Number(c.securityMoney) || 0,
          price: Number(c.price) || 0,
          mode: c.mode
        }));
        
        setAreaId(areaIdValue);
        setMeta(metaValue);
        setCustomers(customersArray);
        const now = Date.now();
        setLastUpdate(new Date(now));
        
        // Update cache
        dashboardCache.set(cacheKey, {
          areaId: areaIdValue,
          meta: metaValue,
          customers: customersArray,
          timestamp: now
        });
      } else {
        console.error("Area not found for slug:", slug);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [slug]);

  // Initial load - only once
  useEffect(() => {
    if (!dataFetchedRef.current) {
      dataFetchedRef.current = true;
      fetchAllData(false);
    }
  }, [fetchAllData]);

  // Calculate statistics (client-side)
  const stats = {
    total: customers.length,
    jar: customers.filter(c => c.type === 'jar').length,
    cooljar: customers.filter(c => c.type === 'cooljar').length,
    totalPendingAmount: customers.reduce((sum, c) => sum + (c.pendingAmount || 0), 0),
    totalAdvance: customers.reduce((sum, c) => sum + (c.advance || 0), 0),
    totalPendingJars: customers.reduce((sum, c) => sum + (c.pendingJars || 0), 0),
    totalSecurity: customers.reduce((sum, c) => sum + (c.securityMoney || 0), 0)
  };

  const handleLogout = async () => {
    try {
      localStorage.removeItem("loggedInArea");
      sessionStorage.removeItem("loggedInArea");
      dashboardCache.clear();
      navigate("/");
    } catch (err) {
      console.error("Logout error:", err);
      navigate("/");
    }
  };

  const handleRefresh = () => {
    fetchAllData(true);
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo-icon">🏪</div>
            <div className="logo-text">
              <h1>Jauhari Enterprises</h1>
              <p>{meta?.name || slug}</p>
            </div>
          </div>
          
          <div className="header-actions">
            <button onClick={handleRefresh} className="refresh-btn" disabled={refreshing} title="Refresh data">
              {refreshing ? '⟳' : '↻'}
            </button>
            <button onClick={handleLogout} className="logout-btn">
              🚪 Logout
            </button>
          </div>
        </div>
      </header>

      {/* Stats Cards - Quick Overview */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-details">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total Customers</span>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">🏺</div>
          <div className="stat-details">
            <span className="stat-value">{stats.jar}</span>
            <span className="stat-label">Jar Customers</span>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon">🥤</div>
          <div className="stat-details">
            <span className="stat-value">{stats.cooljar}</span>
            <span className="stat-label">Cool Jar</span>
          </div>
        </div>
        
        <div className="stat-card warning">
          <div className="stat-icon">⚠️</div>
          <div className="stat-details">
            <span className="stat-value">₹{stats.totalPendingAmount.toLocaleString()}</span>
            <span className="stat-label">Total Pending</span>
          </div>
        </div>
        
        <div className="stat-card success">
          <div className="stat-icon">💰</div>
          <div className="stat-details">
            <span className="stat-value">₹{stats.totalAdvance.toLocaleString()}</span>
            <span className="stat-label">Total Advance</span>
          </div>
        </div>
      </div>

      {/* Summary Cards - Key Metrics */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-icon">📦</div>
          <div className="summary-info">
            <span className="summary-label">Total Pending Jars</span>
            <span className="summary-value">{stats.totalPendingJars}</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">🔒</div>
          <div className="summary-info">
            <span className="summary-label">Total Security</span>
            <span className="summary-value">₹{stats.totalSecurity.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Action Buttons - Main Navigation */}
      <div className="action-buttons">
        <button onClick={() => navigate(`/area/${slug}/add-customer`)} className="action-btn primary">
          <span>➕</span> Add Customer
        </button>
        <button onClick={() => navigate(`/area/${slug}/daily-entry`)} className="action-btn secondary">
          <span>🧾</span> Daily Entry
        </button>
        <button onClick={() => navigate(`/area/${slug}/reports`)} className="action-btn tertiary">
          <span>📄</span> Reports
        </button>
        <button onClick={() => navigate(`/area/${slug}/previous-entries`)} className="action-btn quaternary">
          <span>⏳</span> History
        </button>
      </div>

      {/* Quick Access Section */}
      <div className="quick-access">
        <div className="section-header">
          <h2>📌 Quick Actions</h2>
          {lastUpdate && (
            <span className="last-update">Last updated: {lastUpdate.toLocaleTimeString()}</span>
          )}
        </div>
        
        <div className="quick-grid">
          <div className="quick-card" onClick={() => navigate(`/area/${slug}/daily-entry`)}>
            <div className="quick-icon">🧾</div>
            <div className="quick-text">
              <h3>Daily Entry</h3>
              <p>Record today's transactions</p>
            </div>
            <div className="quick-arrow">→</div>
          </div>
          
          <div className="quick-card" onClick={() => navigate(`/area/${slug}/reports`)}>
            <div className="quick-icon">📊</div>
            <div className="quick-text">
              <h3>View Reports</h3>
              <p>Check customer statements</p>
            </div>
            <div className="quick-arrow">→</div>
          </div>
          
          <div className="quick-card" onClick={() => navigate(`/area/${slug}/add-customer`)}>
            <div className="quick-icon">👤</div>
            <div className="quick-text">
              <h3>Add Customer</h3>
              <p>Register new customer</p>
            </div>
            <div className="quick-arrow">→</div>
          </div>
          
          <div className="quick-card" onClick={() => navigate(`/area/${slug}/previous-entries`)}>
            <div className="quick-icon">📜</div>
            <div className="quick-text">
              <h3>History</h3>
              <p>View past entries</p>
            </div>
            <div className="quick-arrow">→</div>
          </div>
        </div>
      </div>

      {/* Tips Section */}
      <div className="tips-section">
        <div className="tips-header">
          <span>💡</span>
          <h3>Quick Tips</h3>
        </div>
        <ul className="tips-list">
          <li>Use "Daily Entry" to record customer transactions</li>
          <li>Check "Reports" to view customer statements</li>
          <li>Pending amounts show customer dues</li>
          <li>Advance amounts show customer credit</li>
        </ul>
      </div>

      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .dashboard-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 20px;
        }

        /* Header */
        .dashboard-header {
          background: rgba(255,255,255,0.95);
          border-radius: 20px;
          padding: 20px 30px;
          margin-bottom: 25px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.1);
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 20px;
        }

        .logo-section {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .logo-icon {
          font-size: 48px;
          background: linear-gradient(135deg, #667eea, #764ba2);
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 15px;
        }

        .logo-text h1 {
          font-size: 24px;
          color: #333;
          margin: 0;
        }

        .logo-text p {
          color: #666;
          margin: 5px 0 0;
          font-size: 14px;
        }

        .header-actions {
          display: flex;
          gap: 10px;
        }

        .refresh-btn, .logout-btn {
          padding: 10px 20px;
          border: none;
          border-radius: 10px;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.3s;
        }

        .refresh-btn {
          background: #f0f0f0;
          font-size: 20px;
        }

        .refresh-btn:hover:not(:disabled) {
          background: #e0e0e0;
          transform: rotate(180deg);
        }

        .refresh-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .logout-btn {
          background: #dc3545;
          color: white;
        }

        .logout-btn:hover {
          background: #c82333;
          transform: translateY(-2px);
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 15px;
          margin-bottom: 20px;
        }

        .stat-card {
          background: rgba(255,255,255,0.95);
          padding: 15px;
          border-radius: 15px;
          display: flex;
          align-items: center;
          gap: 12px;
          transition: all 0.3s;
        }

        .stat-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 5px 20px rgba(0,0,0,0.15);
        }

        .stat-icon {
          font-size: 32px;
        }

        .stat-details {
          flex: 1;
        }

        .stat-value {
          display: block;
          font-size: 22px;
          font-weight: bold;
          color: #333;
        }

        .stat-label {
          display: block;
          font-size: 11px;
          color: #666;
          margin-top: 3px;
        }

        .stat-card.warning .stat-value {
          color: #dc3545;
        }

        .stat-card.success .stat-value {
          color: #28a745;
        }

        /* Summary Cards */
        .summary-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
          margin-bottom: 25px;
        }

        .summary-card {
          background: rgba(255,255,255,0.95);
          padding: 15px 20px;
          border-radius: 15px;
          display: flex;
          align-items: center;
          gap: 15px;
          transition: all 0.3s;
        }

        .summary-card:hover {
          transform: translateY(-2px);
        }

        .summary-icon {
          font-size: 32px;
        }

        .summary-info {
          flex: 1;
        }

        .summary-label {
          display: block;
          font-size: 12px;
          color: #666;
          margin-bottom: 5px;
        }

        .summary-value {
          display: block;
          font-size: 20px;
          font-weight: bold;
          color: #333;
        }

        /* Action Buttons */
        .action-buttons {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 15px;
          margin-bottom: 25px;
        }

        .action-btn {
          padding: 14px;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          color: white;
        }

        .action-btn.primary { background: linear-gradient(135deg, #28a745, #20c997); }
        .action-btn.secondary { background: linear-gradient(135deg, #007bff, #00b4d8); }
        .action-btn.tertiary { background: linear-gradient(135deg, #ffc107, #fd7e14); }
        .action-btn.quaternary { background: linear-gradient(135deg, #6c757d, #495057); }

        .action-btn:hover {
          transform: translateY(-2px);
          filter: brightness(1.05);
        }

        /* Quick Access */
        .quick-access {
          background: rgba(255,255,255,0.95);
          border-radius: 20px;
          padding: 20px;
          margin-bottom: 20px;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #f0f0f0;
        }

        .section-header h2 {
          margin: 0;
          font-size: 18px;
          color: #333;
        }

        .last-update {
          font-size: 11px;
          color: #999;
        }

        .quick-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 15px;
        }

        .quick-card {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 15px;
          display: flex;
          align-items: center;
          gap: 15px;
          cursor: pointer;
          transition: all 0.3s;
          border: 1px solid #e0e0e0;
        }

        .quick-card:hover {
          transform: translateX(5px);
          background: white;
          border-color: #667eea;
          box-shadow: 0 2px 8px rgba(102,126,234,0.2);
        }

        .quick-icon {
          font-size: 32px;
        }

        .quick-text {
          flex: 1;
        }

        .quick-text h3 {
          margin: 0 0 4px 0;
          font-size: 14px;
          color: #333;
        }

        .quick-text p {
          margin: 0;
          font-size: 11px;
          color: #666;
        }

        .quick-arrow {
          font-size: 18px;
          color: #667eea;
          opacity: 0;
          transition: opacity 0.3s;
        }

        .quick-card:hover .quick-arrow {
          opacity: 1;
        }

        /* Tips Section */
        .tips-section {
          background: rgba(255,255,255,0.95);
          border-radius: 20px;
          padding: 20px;
        }

        .tips-header {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 15px;
          padding-bottom: 10px;
          border-bottom: 2px solid #f0f0f0;
        }

        .tips-header span {
          font-size: 24px;
        }

        .tips-header h3 {
          margin: 0;
          font-size: 16px;
          color: #333;
        }

        .tips-list {
          list-style: none;
          padding: 0;
        }

        .tips-list li {
          padding: 8px 0;
          padding-left: 20px;
          position: relative;
          font-size: 13px;
          color: #555;
        }

        .tips-list li:before {
          content: "•";
          position: absolute;
          left: 0;
          color: #667eea;
          font-weight: bold;
          font-size: 16px;
        }

        /* Loading Screen */
        .loading-screen {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
        }

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid rgba(255,255,255,0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 15px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .loading-screen p {
          color: white;
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .dashboard-container {
            padding: 10px;
          }

          .dashboard-header {
            padding: 15px;
          }

          .logo-icon {
            width: 45px;
            height: 45px;
            font-size: 28px;
          }

          .logo-text h1 {
            font-size: 18px;
          }

          .logo-text p {
            font-size: 12px;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }

          .stat-card {
            padding: 12px;
          }

          .stat-icon {
            font-size: 24px;
          }

          .stat-value {
            font-size: 18px;
          }

          .summary-cards {
            grid-template-columns: 1fr;
          }

          .action-buttons {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }

          .action-btn {
            padding: 12px;
            font-size: 12px;
          }

          .quick-grid {
            grid-template-columns: 1fr;
          }

          .quick-card {
            padding: 12px;
          }

          .quick-icon {
            font-size: 28px;
          }

          .quick-text h3 {
            font-size: 13px;
          }

          .section-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .tips-list li {
            font-size: 12px;
          }
        }

        /* Small Mobile */
        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: 1fr;
          }

          .action-buttons {
            grid-template-columns: 1fr;
          }

          .header-content {
            flex-direction: column;
            text-align: center;
          }

          .logo-section {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
}